import React, { useState } from 'react';
import { PassiveSkill } from '../types';
import { passiveSkillService } from '../services/passiveSkillService';
import { useLanguage } from '../contexts/LanguageContext';
import HeatMapCalendar from './HeatMapCalendar';

interface PassiveSkillCardProps {
  skill: PassiveSkill;
  onComplete: () => void;
  onToggleActive: () => void;
  onDelete: () => void;
}

const PassiveSkillCard: React.FC<PassiveSkillCardProps> = ({
  skill,
  onComplete,
  onToggleActive,
  onDelete
}) => {
  const { t } = useLanguage();
  const [showHeatMap, setShowHeatMap] = useState(false);
  
  const canComplete = passiveSkillService.canCompleteToday(skill);
  const isCustom = skill.frequency === 'custom';
  const weekday = new Date().getDay();
  const isScheduledToday = isCustom && skill.allowedWeekdays && skill.allowedWeekdays.length > 0
    ? skill.allowedWeekdays.includes(weekday)
    : true;
  const weeklyProgress = passiveSkillService.getWeeklyCompletionRate(skill);
  
  // Calculate expected completions based on user's goals
  const getExpectedCompletions = (): number => {
    if (!skill.targetDuration) {
      // No target duration set, use original logic
      return 365; // Maximum for level 10
    }

    const duration = skill.targetDuration;

    switch (skill.frequency) {
      case 'daily':
        return duration; // One completion per day

      case 'weekly':
        const weeks = Math.ceil(duration / 7);
        const targetDaysPerWeek = skill.targetDaysPerWeek || 3;
        return weeks * targetDaysPerWeek; // Completions per week * number of weeks

      case 'custom':
        if (!skill.allowedWeekdays || skill.allowedWeekdays.length === 0) {
          return duration; // Fallback to daily if no weekdays specified
        }

        // Count how many days within the duration fall on allowed weekdays
        let count = 0;
        const startDate = new Date(skill.createdAt);
        for (let i = 0; i < duration; i++) {
          const checkDate = new Date(startDate);
          checkDate.setDate(startDate.getDate() + i);
          const dayOfWeek = checkDate.getDay(); // 0=Sun, 1=Mon, etc.
          if (skill.allowedWeekdays.includes(dayOfWeek)) {
            count++;
          }
        }
        return count;

      default:
        return duration;
    }
  };

  // Get level thresholds based on expected completions
  const getLevelThresholds = () => {
    const expectedCompletions = getExpectedCompletions();

    if (!skill.targetDuration) {
      // No target duration, use original fixed thresholds
      return {
        current: [0, 7, 14, 30, 60, 90, 150, 200, 270, 365][skill.level - 1] || 0,
        next: [7, 14, 30, 60, 90, 150, 200, 270, 365, 999][skill.level - 1] || 999
      };
    }

    // Calculate thresholds the same way as the service
    const thresholds: Record<number, number> = {};
    for (let level = 1; level <= 10; level++) {
      if (level === 1) {
        thresholds[level] = 0; // Always start at level 1 with 0 completions
      } else if (level === 2) {
        thresholds[level] = 1; // Level 2 requires 1 completion
      } else {
        // For very short goals (1-7 days), use minimal progression to prevent instant max level
        if (skill.targetDuration <= 7) {
          // Cap at reasonable progression: Level 10 requires at least 7 completions for short goals
          const minTarget = Math.max(skill.targetDuration, 7);
          const remainingLevels = 8; // Levels 3-10
          const remainingCompletions = minTarget - 1; // Subtract the 1 for level 2
          const progressRatio = (level - 2) / remainingLevels; // Progress from level 2 to 10
          thresholds[level] = 1 + Math.round(progressRatio * remainingCompletions);
        } else {
          // Normal linear distribution for longer goals
          const remainingLevels = 8; // Levels 3-10
          const remainingCompletions = skill.targetDuration - 1; // Subtract the 1 for level 2
          const progressRatio = (level - 2) / remainingLevels; // Progress from level 2 to 10
          thresholds[level] = 1 + Math.round(progressRatio * remainingCompletions);
        }
      }
    }
    // Ensure level 10 threshold is exactly the target duration
    thresholds[10] = skill.targetDuration;

    return {
      current: thresholds[skill.level] || 0,
      next: thresholds[skill.level + 1] || expectedCompletions
    };
  };

  const thresholds = getLevelThresholds();
  const levelProgress = skill.level === 10 ? 1 : (skill.totalCompletions - thresholds.current) / (thresholds.next - thresholds.current);

  // Calculate milestone progress for goal-based rewards
  const getMilestoneProgress = () => {
    if (!skill.targetDuration) return null;

    const expectedCompletions = getExpectedCompletions();
    const completionPercentage = (skill.totalCompletions / expectedCompletions) * 100;

    const milestones = [
      { percent: 25, label: "25%", color: "blue", earned: completionPercentage >= 25 },
      { percent: 50, label: "50%", color: "green", earned: completionPercentage >= 50 },
      { percent: 75, label: "75%", color: "orange", earned: completionPercentage >= 75 },
      { percent: 100, label: "100%", color: "purple", earned: completionPercentage >= 100 }
    ];

    return {
      percentage: completionPercentage,
      milestones: milestones.filter(m => m.earned),
      nextMilestone: milestones.find(m => !m.earned)
    };
  };

  const milestoneData = getMilestoneProgress();

  // Check if skill has reached its target duration
  const getCompletionStatus = () => {
    if (!skill.targetDuration) return null;

    const daysSinceCreation = Math.floor((Date.now() - skill.createdAt) / (1000 * 60 * 60 * 24));
    const isCompleted = daysSinceCreation >= skill.targetDuration;

    if (isCompleted) {
      // Check if it was a perfect goal (no missed days within the goal period)
      const expectedCompletions = getExpectedCompletions();
      const perfectGoal = skill.totalCompletions >= expectedCompletions;

      return {
        status: 'completed',
        daysOverdue: daysSinceCreation - skill.targetDuration,
        message: `🎉 Goal completed! ${skill.targetDuration} day challenge finished!`,
        perfectGoal: perfectGoal
      };
    }

    return null;
  };

  const completionStatus = getCompletionStatus();

  // Smart reminders for missed days
  const getSmartReminder = () => {
    if (!skill.isActive) return null;

    // Don't show reminders for completed goals
    if (completionStatus) return null;

    const today = new Date();
    today.setHours(0, 0, 0, 0);

    // Only show reminders for skills that have been active for at least 2 days
    const daysSinceCreation = Math.floor((today.getTime() - skill.createdAt) / (1000 * 60 * 60 * 24));
    if (daysSinceCreation < 2) return null;

    const yesterday = new Date(today);
    yesterday.setDate(today.getDate() - 1);

    const twoDaysAgo = new Date(today);
    twoDaysAgo.setDate(today.getDate() - 2);

    // Don't show reminders for dates before skill creation
    if (yesterday.getTime() < skill.createdAt || twoDaysAgo.getTime() < skill.createdAt) {
      return null;
    }

    const yesterdayStr = dateToString(yesterday);
    const twoDaysAgoStr = dateToString(twoDaysAgo);

    // Check if yesterday should have been completed based on frequency
    const dayOfWeekYesterday = yesterday.getDay(); // 0=Sun, 1=Mon, etc.
    const shouldCompleteYesterday =
      skill.frequency === 'daily' ||
      (skill.frequency === 'weekly' && skill.targetDaysPerWeek && skill.targetDaysPerWeek > 0) ||
      (skill.frequency === 'custom' && skill.allowedWeekdays && skill.allowedWeekdays.includes(dayOfWeekYesterday));

    const yesterdayCompleted = completionDates.includes(yesterdayStr);

    // Check if 2 days ago should have been completed
    const dayOfWeekTwoDaysAgo = twoDaysAgo.getDay();
    const shouldCompleteTwoDaysAgo =
      skill.frequency === 'daily' ||
      (skill.frequency === 'weekly' && skill.targetDaysPerWeek && skill.targetDaysPerWeek > 0) ||
      (skill.frequency === 'custom' && skill.allowedWeekdays && skill.allowedWeekdays.includes(dayOfWeekTwoDaysAgo));

    const twoDaysAgoCompleted = completionDates.includes(twoDaysAgoStr);

    if (shouldCompleteYesterday && !yesterdayCompleted) {
      return {
        type: 'missed_yesterday',
        message: 'You missed yesterday - want to catch up?',
        priority: 'high'
      };
    }

    if (shouldCompleteTwoDaysAgo && !twoDaysAgoCompleted && shouldCompleteYesterday && !yesterdayCompleted) {
      return {
        type: 'missed_two_days',
        message: 'Two days behind - every step counts!',
        priority: 'high'
      };
    }

    // Check for streak protection
    if (skill.currentStreak >= 3 && shouldCompleteYesterday && !yesterdayCompleted) {
      return {
        type: 'streak_at_risk',
        message: `Don't break your ${skill.currentStreak}-day streak!`,
        priority: 'high'
      };
    }

    // Check for goal deadline approaching
    if (skill.targetDuration && milestoneData) {
      const daysLeft = skill.targetDuration - daysSinceCreation;

      if (daysLeft <= 3 && milestoneData.percentage < 75) {
        return {
          type: 'goal_deadline',
          message: `Only ${daysLeft} days left in your goal!`,
          priority: 'medium'
        };
      }
    }

    return null;
  };

  // Helper function for date formatting
  const dateToString = (date: Date): string => {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  };

  const completionDates = skill.completionHistory;

  const smartReminder = getSmartReminder();

  // Progress predictions
  const getProgressPrediction = () => {
    if (skill.level === 10 || completionStatus) return null; // Max level reached

    const daysSinceCreation = Math.floor((Date.now() - skill.createdAt) / (1000 * 60 * 60 * 24));
    if (daysSinceCreation < 1) return null; // Not enough data

    // Calculate daily completion rate
    const dailyRate = skill.totalCompletions / daysSinceCreation;

    // Calculate completions needed for next level
    const completionsNeeded = thresholds.next - skill.totalCompletions;

    if (completionsNeeded <= 0) return null; // Should not happen

    // Estimate days needed at current rate
    const estimatedDays = Math.ceil(completionsNeeded / dailyRate);

    // Calculate estimated completion date
    const estimatedDate = new Date();
    estimatedDate.setDate(estimatedDate.getDate() + estimatedDays);

    return {
      daysUntilNextLevel: estimatedDays,
      estimatedDate: estimatedDate,
      currentRate: dailyRate,
      completionsNeeded: completionsNeeded
    };
  };

  const progressPrediction = getProgressPrediction();

  // Category colors with glossy backgrounds
  const categoryColors: Record<string, { 
    border: string; 
    text: string; 
    accent: string;
    bg: string;
    glow: string;
  }> = {
    physical: { 
      border: 'border-red-500/40', 
      text: 'text-red-400', 
      accent: 'from-red-600 to-red-700',
      bg: 'from-red-950/40 via-red-900/30 to-red-950/40',
      glow: 'shadow-[0_0_30px_rgba(239,68,68,0.2)]'
    },
    mental: { 
      border: 'border-purple-500/40', 
      text: 'text-purple-400', 
      accent: 'from-purple-600 to-purple-700',
      bg: 'from-purple-950/40 via-purple-900/30 to-purple-950/40',
      glow: 'shadow-[0_0_30px_rgba(168,85,247,0.2)]'
    },
    social: { 
      border: 'border-green-500/40', 
      text: 'text-green-400', 
      accent: 'from-green-600 to-green-700',
      bg: 'from-green-950/40 via-green-900/30 to-green-950/40',
      glow: 'shadow-[0_0_30px_rgba(34,197,94,0.2)]'
    },
    spiritual: { 
      border: 'border-cyan-500/40', 
      text: 'text-cyan-400', 
      accent: 'from-cyan-600 to-cyan-700',
      bg: 'from-cyan-950/40 via-cyan-900/30 to-cyan-950/40',
      glow: 'shadow-[0_0_30px_rgba(6,182,212,0.2)]'
    },
    productivity: { 
      border: 'border-yellow-500/40', 
      text: 'text-yellow-400', 
      accent: 'from-yellow-600 to-yellow-700',
      bg: 'from-yellow-950/40 via-yellow-900/30 to-yellow-950/40',
      glow: 'shadow-[0_0_30px_rgba(234,179,8,0.2)]'
    },
  };

  const colors = categoryColors[skill.category] || categoryColors.physical;
  
  const borderRightColors: Record<string, string> = {
    physical: 'border-r-red-700/70',
    mental: 'border-r-purple-700/70',
    social: 'border-r-green-700/70',
    spiritual: 'border-r-cyan-700/70',
    productivity: 'border-r-yellow-700/70',
  };
  
  const borderBottomColors: Record<string, string> = {
    physical: 'border-b-red-700/70',
    mental: 'border-b-purple-700/70',
    social: 'border-b-green-700/70',
    spiritual: 'border-b-cyan-700/70',
    productivity: 'border-b-yellow-700/70',
  };
  
  const completeButtonBorderColors: Record<string, string> = {
    physical: 'border-red-500/40 border-r-red-700/60 border-b-red-700/60',
    mental: 'border-purple-500/40 border-r-purple-700/60 border-b-purple-700/60',
    social: 'border-green-500/40 border-r-green-700/60 border-b-green-700/60',
    spiritual: 'border-cyan-500/40 border-r-cyan-700/60 border-b-cyan-700/60',
    productivity: 'border-yellow-500/40 border-r-yellow-700/60 border-b-yellow-700/60',
  };

  return (
    <div
      className={`bg-gradient-to-b ${colors.bg} backdrop-blur-sm border-t-2 border-l-2 ${colors.border} border-r-2 border-b-2 ${borderRightColors[skill.category as keyof typeof borderRightColors] || borderRightColors.physical} ${borderBottomColors[skill.category as keyof typeof borderBottomColors] || borderBottomColors.physical} rounded-3xl px-2 sm:px-3 py-3 sm:py-5 transition-all hover:scale-[1.01] sm:hover:scale-[1.02] ${!skill.isActive ? 'opacity-60' : ''} ${skill.level === 10 && skill.targetDuration ? 'ring-2 ring-yellow-400/50 animate-pulse' : ''}`}
      style={{
        boxShadow: skill.category === 'physical' 
          ? '0 8px 24px rgba(239, 68, 68, 0.4), 0 2px 8px rgba(239, 68, 68, 0.3), inset 0 2px 4px rgba(239, 68, 68, 0.15), inset 0 -3px 8px rgba(153, 27, 27, 0.4)'
          : skill.category === 'mental'
          ? '0 8px 24px rgba(168, 85, 247, 0.4), 0 2px 8px rgba(168, 85, 247, 0.3), inset 0 2px 4px rgba(168, 85, 247, 0.15), inset 0 -3px 8px rgba(88, 28, 135, 0.4)'
          : skill.category === 'social'
          ? '0 8px 24px rgba(34, 197, 94, 0.4), 0 2px 8px rgba(34, 197, 94, 0.3), inset 0 2px 4px rgba(34, 197, 94, 0.15), inset 0 -3px 8px rgba(5, 78, 59, 0.4)'
          : skill.category === 'spiritual'
          ? '0 8px 24px rgba(6, 182, 212, 0.4), 0 2px 8px rgba(6, 182, 212, 0.3), inset 0 2px 4px rgba(6, 182, 212, 0.15), inset 0 -3px 8px rgba(17, 94, 89, 0.4)'
          : '0 8px 24px rgba(234, 179, 8, 0.4), 0 2px 8px rgba(234, 179, 8, 0.3), inset 0 2px 4px rgba(234, 179, 8, 0.15), inset 0 -3px 8px rgba(113, 63, 18, 0.4)'
      }}
    >
      {/* Status Badges */}
      <div className="absolute top-2 right-2 flex flex-col gap-1">
        {!skill.isActive && (
          <div className="bg-gray-800 text-gray-400 px-2 py-1 rounded text-xs font-orbitron uppercase">
            {t.passiveSkills.paused}
          </div>
        )}
        {completionStatus && (
          <div className="bg-green-900/80 border border-green-500/50 text-green-300 px-2 py-1 rounded text-xs font-orbitron uppercase">
            Goal Complete! 🎉
          </div>
        )}
        {skill.level === 10 && skill.targetDuration && (
          <div className="bg-gradient-to-r from-yellow-600/90 to-orange-600/90 border border-yellow-400/70 text-yellow-100 px-2 py-1 rounded text-xs font-orbitron uppercase animate-pulse">
            MAX LEVEL! ⭐
          </div>
        )}
      </div>

      {/* Header */}
      <div className="flex items-start gap-2 sm:gap-3 mb-3 sm:mb-4">
        <span className="text-3xl sm:text-4xl">{skill.icon}</span>
        <div className="flex-1">
          <h3 className="text-lg sm:text-xl font-orbitron font-bold text-white mb-0.5 sm:mb-1">{skill.name}</h3>
          <p className="text-gray-400 font-rajdhani text-xs sm:text-sm line-clamp-2">{skill.description}</p>
        </div>
      </div>

      {/* Level & Streak Display */}
      <div className="grid grid-cols-2 gap-2 sm:gap-3 mb-3 sm:mb-4">
        {/* Level */}
        <div 
          className="bg-gradient-to-b from-slate-800/50 via-slate-900/40 to-slate-950/50 backdrop-blur-sm border-t-2 border-l-2 border-slate-500/30 border-r-2 border-b-2 border-r-slate-700/60 border-b-slate-700/60 rounded-2xl p-2.5 sm:p-3"
          style={{
            boxShadow: '0 3px 8px rgba(0, 0, 0, 0.3), inset 0 1px 2px rgba(255, 255, 255, 0.05)'
          }}
        >
          <div className="flex items-center justify-between mb-2">
            <span className="text-gray-400 font-rajdhani text-xs uppercase">{t.passiveSkills.level}</span>
            <span className={`font-orbitron font-bold ${colors.text} text-lg`}>{skill.level}</span>
          </div>
          <div className="w-full bg-slate-800/70 rounded-full h-2 overflow-hidden border border-slate-600/50">
            <div
              className={`h-full bg-gradient-to-r ${colors.accent} transition-all duration-500`}
              style={{ width: `${levelProgress * 100}%` }}
            />
          </div>
          <p className="text-xs text-gray-500 font-rajdhani mt-1">
            {skill.level === 10 ? t.passiveSkills.max : `${skill.totalCompletions}/${thresholds.next}`}
          </p>
          {progressPrediction && (
            <p className="text-xs text-purple-400 font-rajdhani mt-1">
              {progressPrediction.daysUntilNextLevel === 1
                ? 'Tomorrow!'
                : `${progressPrediction.daysUntilNextLevel} days at current pace`}
            </p>
          )}
        </div>

        {/* Streak */}
        <div 
          className="bg-gradient-to-b from-slate-800/50 via-slate-900/40 to-slate-950/50 backdrop-blur-sm border-t-2 border-l-2 border-slate-500/30 border-r-2 border-b-2 border-r-slate-700/60 border-b-slate-700/60 rounded-2xl p-2.5 sm:p-3"
          style={{
            boxShadow: '0 3px 8px rgba(0, 0, 0, 0.3), inset 0 1px 2px rgba(255, 255, 255, 0.05)'
          }}
        >
          <div className="flex items-center justify-between mb-2">
            <span className="text-gray-400 font-rajdhani text-xs uppercase">{t.passiveSkills.streak}</span>
            <span className="font-orbitron font-bold text-orange-400 text-lg flex items-center gap-1">
              <span>🔥</span>
              {skill.currentStreak}
            </span>
          </div>
          <p className="text-xs text-gray-500 font-rajdhani">
            {t.passiveSkills.best} {skill.longestStreak}
          </p>
          <p className="text-xs text-gray-500 font-rajdhani">
            {t.passiveSkills.total} {skill.totalCompletions}
          </p>
        </div>
      </div>

      {/* Max Level Celebration */}
      {skill.level === 10 && skill.targetDuration && (
        <div className="mb-3 sm:mb-4">
          <div className="bg-gradient-to-r from-yellow-950/40 via-orange-950/30 to-yellow-950/40 border-t-2 border-l-2 border-yellow-500/40 border-r-2 border-b-2 border-r-yellow-700/60 border-b-yellow-700/60 rounded-2xl p-3 animate-pulse">
            <div className="flex items-center gap-2 mb-2">
              <span className="text-2xl animate-bounce">⭐</span>
              <span className="text-sm font-orbitron text-yellow-400 uppercase">Master Achieved!</span>
              <span className="text-2xl animate-bounce">⭐</span>
            </div>
            <p className="text-xs text-yellow-300 font-rajdhani text-center">
              {skill.targetDuration} day challenge conquered! You've reached the pinnacle of this skill. 🏆
            </p>
          </div>
        </div>
      )}

      {/* Milestone Rewards - Only show if target duration is set */}
      {milestoneData && (
        <div className="mb-3 sm:mb-4">
          {completionStatus ? (
            // Show completion celebration
            <div className="bg-gradient-to-r from-green-950/40 to-emerald-950/40 border-t-2 border-l-2 border-green-500/40 border-r-2 border-b-2 border-r-green-700/60 border-b-green-700/60 rounded-2xl p-3">
              <div className="flex items-center gap-2 mb-2">
                <span className="text-lg">🎉</span>
                <span className="text-sm font-orbitron text-green-400 uppercase">Goal Achieved!</span>
              </div>
              <p className="text-xs text-green-300 font-rajdhani">
                {completionStatus.message}
              </p>
              {completionStatus.daysOverdue > 0 && (
                <p className="text-xs text-green-400 font-rajdhani mt-1">
                  Completed {completionStatus.daysOverdue} day{completionStatus.daysOverdue !== 1 ? 's' : ''} ago
                </p>
              )}
            </div>
          ) : (
            // Show regular milestone progress
            <>
              <div className="flex items-center gap-2 mb-2">
                <span className="text-xs font-rajdhani text-gray-400 uppercase">Goal Progress</span>
                <span className="text-xs font-orbitron text-purple-400">{Math.round(milestoneData.percentage)}%</span>
              </div>
              <div className="flex flex-wrap gap-1">
                {milestoneData.milestones.map((milestone, index) => (
                  <div
                    key={index}
                    className={`px-2 py-1 rounded-lg border text-xs font-orbitron font-bold ${
                      milestone.color === 'blue'
                        ? 'bg-blue-950/50 border-blue-500/50 text-blue-400'
                        : milestone.color === 'green'
                        ? 'bg-green-950/50 border-green-500/50 text-green-400'
                        : milestone.color === 'orange'
                        ? 'bg-orange-950/50 border-orange-500/50 text-orange-400'
                        : 'bg-purple-950/50 border-purple-500/50 text-purple-400'
                    }`}
                  >
                    {milestone.label}
                  </div>
                ))}
                {milestoneData.nextMilestone && (
                  <div className="px-2 py-1 rounded-lg border border-gray-600/50 bg-gray-900/50 text-gray-500 text-xs font-orbitron">
                    Next: {milestoneData.nextMilestone.label}
                  </div>
                )}
              </div>
            </>
          )}
        </div>
      )}

      {/* Smart Reminder */}
      {smartReminder && (
        <div className={`mb-3 sm:mb-4 p-2.5 sm:p-3 rounded-2xl border-t-2 border-l-2 border-r-2 border-b-2 ${
          smartReminder.priority === 'high'
            ? 'bg-orange-950/30 border-orange-500/40 border-r-orange-700/60 border-b-orange-700/60'
            : 'bg-blue-950/30 border-blue-500/40 border-r-blue-700/60 border-b-blue-700/60'
        }`}>
          <div className="flex items-center gap-2">
            <span className={`text-lg ${smartReminder.priority === 'high' ? 'text-orange-400' : 'text-blue-400'}`}>
              {smartReminder.priority === 'high' ? '⚠️' : '💡'}
            </span>
            <span className={`text-sm font-rajdhani ${
              smartReminder.priority === 'high' ? 'text-orange-300' : 'text-blue-300'
            }`}>
              {smartReminder.message}
            </span>
          </div>
        </div>
      )}

      {/* Stat Boost */}
      <div 
        className="bg-gradient-to-b from-emerald-950/40 via-green-950/30 to-emerald-950/50 backdrop-blur-sm border-t-2 border-l-2 border-emerald-500/40 border-r-2 border-b-2 border-r-emerald-700/60 border-b-emerald-700/60 rounded-2xl p-3 mb-4"
        style={{
          boxShadow: '0 3px 8px rgba(16, 185, 129, 0.3), inset 0 1px 2px rgba(52, 211, 153, 0.1)'
        }}
      >
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <span className="text-emerald-400">✨</span>
            <span className="text-gray-300 font-rajdhani text-sm">{t.stats[skill.statBoost.stat as keyof typeof t.stats]} {t.passiveSkills.boost}</span>
          </div>
          <div className="text-right">
            <span className="text-emerald-400 font-orbitron font-bold text-sm">
              +{(skill.statBoost.percentBoost * 100).toFixed(1)}%
            </span>
            {skill.targetDuration && (
              <div className="text-xs text-purple-400 font-rajdhani mt-1">
                {skill.targetDuration} day commitment
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Weekly Progress */}
      <div className="mb-3 sm:mb-4">
        <div className="flex items-center justify-between mb-2">
          <span className="text-gray-400 font-rajdhani text-[11px] sm:text-xs uppercase">{t.passiveSkills.thisWeek}</span>
          <span className="text-gray-300 font-rajdhani text-xs sm:text-sm">
            {weeklyProgress.completed}/{weeklyProgress.target} {t.calendar.days}
          </span>
        </div>
        <div className="w-full bg-slate-700/50 rounded-full h-2 overflow-hidden border border-slate-600/50">
          <div
            className="h-full bg-gradient-to-r from-blue-500 via-cyan-500 to-blue-500 transition-all duration-500"
            style={{ width: `${weeklyProgress.rate * 100}%` }}
          />
        </div>
      </div>

      {/* Heat Map Toggle */}
      <button
        onClick={() => setShowHeatMap(!showHeatMap)}
        className={`w-full text-left px-2.5 sm:px-4 py-2.5 sm:py-3 mb-2.5 sm:mb-3 flex items-center justify-between transition-all rounded-2xl border-t-2 border-l-2 border-r-2 border-b-2 relative group ${
          showHeatMap 
            ? 'bg-gradient-to-b from-blue-800/50 via-blue-900/40 to-blue-950/50 border-blue-500/40 border-r-blue-700/60 border-b-blue-700/60'
            : 'bg-gradient-to-b from-slate-800/30 via-slate-900/30 to-slate-950/30 border-slate-500/40 border-r-slate-700/60 border-b-slate-700/60 hover:border-blue-500/50'
        }`}
        style={{
          boxShadow: showHeatMap 
            ? '0 4px 12px rgba(59, 130, 246, 0.3), inset 0 1px 2px rgba(100, 150, 255, 0.1)'
            : '0 3px 8px rgba(0, 0, 0, 0.3), inset 0 1px 2px rgba(255, 255, 255, 0.05)'
        }}
      >
        <span className="flex items-center gap-2 font-rajdhani text-xs sm:text-sm relative z-10">
          <img 
            src="/icons/icon-passive-disclosure.png" 
            alt="Toggle"
            className={`w-4 h-4 sm:w-5 sm:h-5 object-contain transition-transform ${showHeatMap ? 'rotate-90' : ''}`}
            style={{ filter: 'drop-shadow(0 0 6px rgba(59, 130, 246, 0.8))' }}
          />
          <span className={`font-semibold ${showHeatMap ? 'text-system-blue' : 'text-gray-300 group-hover:text-white'}`}>
            {t.passiveSkills.completionHistory}
          </span>
        </span>
        <span className={`text-[11px] sm:text-xs font-orbitron font-bold relative z-10 ${showHeatMap ? 'text-system-blue' : 'text-gray-400 group-hover:text-white'}`}>
          {skill.completionHistory.length} {t.calendar.days}
        </span>
        {showHeatMap && (
          <div className="absolute inset-0 bg-gradient-to-b from-white/10 via-transparent to-black/20 rounded-2xl pointer-events-none"></div>
        )}
      </button>

      {/* Heat Map Calendar */}
      {showHeatMap && (
        <div 
          className="mb-3 sm:mb-4 p-2.5 sm:p-3 bg-gradient-to-b from-slate-800/50 via-slate-900/40 to-slate-950/50 backdrop-blur-sm border-t-2 border-l-2 border-slate-500/30 border-r-2 border-b-2 border-r-slate-700/60 border-b-slate-700/60 rounded-2xl"
          style={{
            boxShadow: '0 3px 8px rgba(0, 0, 0, 0.3), inset 0 1px 2px rgba(255, 255, 255, 0.05)'
          }}
        >
          <HeatMapCalendar
            completionDates={skill.completionHistory}
            startDate={new Date(skill.createdAt).toISOString().split('T')[0]}
            targetDuration={skill.targetDuration}
            frequency={skill.frequency}
            targetDaysPerWeek={skill.targetDaysPerWeek}
            allowedWeekdays={skill.allowedWeekdays}
          />
        </div>
      )}

      {/* Action Buttons */}
      <div className="flex gap-2">
        {/* Complete Button */}
        <button
          onClick={onComplete}
          disabled={!canComplete || !skill.isActive}
          className={`flex-1 font-orbitron font-bold py-2 sm:py-3 px-2.5 sm:px-4 rounded-2xl uppercase text-[11px] sm:text-sm transition-all border-t-2 border-l-2 border-r-2 border-b-2 relative ${
            canComplete && skill.isActive
              ? `bg-gradient-to-b ${colors.accent} hover:scale-105 text-white ${completeButtonBorderColors[skill.category] || completeButtonBorderColors.physical}`
              : 'bg-gradient-to-b from-slate-800/40 via-slate-900/40 to-slate-950/50 border-slate-700/60 text-gray-500 cursor-not-allowed'
          }`}
          style={canComplete && skill.isActive ? {
            boxShadow: '0 4px 12px rgba(0, 0, 0, 0.4), inset 0 1px 2px rgba(255, 255, 255, 0.1)'
          } : {
            boxShadow: '0 3px 8px rgba(0, 0, 0, 0.3), inset 0 1px 2px rgba(255, 255, 255, 0.04)'
          }}
        >
          <span className="relative z-10">
            {!skill.isActive
              ? t.passiveSkills.paused
              : canComplete
                ? t.passiveSkills.complete
                : (isCustom && !isScheduledToday ? t.passiveSkills.notScheduledToday : t.passiveSkills.doneToday)}
          </span>
          {canComplete && skill.isActive && (
            <div className="absolute inset-0 bg-gradient-to-b from-white/10 via-transparent to-black/20 rounded-2xl pointer-events-none"></div>
          )}
        </button>

        {/* More Actions Dropdown */}
        <div className="relative group">
          <button 
            className="bg-gradient-to-b from-slate-800/40 via-slate-900/40 to-slate-950/50 border-t-2 border-l-2 border-slate-500/40 border-r-2 border-b-2 border-r-slate-700/60 border-b-slate-700/60 hover:border-slate-600 text-white p-2.5 sm:p-3 rounded-2xl transition-all"
            style={{
              boxShadow: '0 3px 8px rgba(0, 0, 0, 0.3), inset 0 1px 2px rgba(255, 255, 255, 0.05)'
            }}
          >
            <span className="text-lg">⋮</span>
          </button>
          <div 
            className="absolute right-0 bottom-full mb-2 w-48 bg-gradient-to-b from-slate-900/95 via-slate-950/95 to-slate-950/95 backdrop-blur-sm border-t-2 border-l-2 border-slate-500/50 border-r-2 border-b-2 border-r-slate-700/70 border-b-slate-700/70 rounded-2xl opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all z-10 overflow-hidden"
            style={{
              boxShadow: '0 8px 24px rgba(0, 0, 0, 0.6), inset 0 1px 2px rgba(255, 255, 255, 0.08)'
            }}
          >
            <button
              onClick={onToggleActive}
              className="w-full text-left px-4 py-2 hover:bg-slate-700/30 text-white font-rajdhani transition-colors text-sm"
            >
              {skill.isActive ? t.passiveSkills.pause : t.passiveSkills.resume}
            </button>
            <div className="border-t border-slate-600/40"></div>
            <button
              onClick={onDelete}
              className="w-full text-left px-4 py-2 hover:bg-red-900/30 text-red-400 font-rajdhani transition-colors text-sm"
            >
              {t.passiveSkills.deleteSkill}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PassiveSkillCard;
